import {
  Workflow, Cloud, Code2, Bot, Database, GitBranch, LifeBuoy, Compass,
  ShieldCheck, Gauge, Users, Layers, Rocket, LineChart, Building2, Landmark,
  HeartPulse, ShoppingCart, Factory, Truck,
} from "lucide-react";

export const COMPANY = {
  name: "FusionTech Consulting",
  short: "FusionTech",
  domain: "fusiontechconsulting.com",
  url: "https://fusiontechconsulting.com",
  tagline: "We turn complex enterprise technology into a durable business advantage.",
  email: "hello@fusiontechconsulting.com",
  phone: "+1 (000) 000-0000", // PLACEHOLDER — replace with a monitored line
  address: "Global · Remote-first", // PLACEHOLDER — replace with registered address
  social: {
    linkedin: "#", twitter: "#", github: "#",
  },
};

export const NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Technologies", to: "/technologies" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "About", to: "/about" },
  { label: "Blog", to: "/blog" },
  { label: "Careers", to: "/careers" },
];

export const SERVICES = [
  {
    slug: "enterprise-integration",
    icon: Workflow,
    title: "Enterprise Integration",
    excerpt: "Connect core systems once, then reuse those connections everywhere — so new products ship in weeks, not quarters.",
    capabilities: ["MuleSoft Development", "API-led Connectivity", "API Strategy", "API Management"],
    outcomes: ["New products reach market faster", "Integration effort is reused, not rebuilt", "A single, governed view of enterprise data"],
    span: "lg:col-span-7",
  },
  {
    slug: "cloud-consulting",
    icon: Cloud,
    title: "Cloud Consulting",
    excerpt: "Move critical workloads to the cloud with a plan that protects uptime, controls spend and stands up to audit.",
    capabilities: ["AWS", "Microsoft Azure", "Google Cloud"],
    outcomes: ["Lower, more predictable run costs", "Capacity that scales with demand", "Fewer outages and faster recovery"],
    span: "lg:col-span-5",
  },
  {
    slug: "custom-software-development",
    icon: Code2,
    title: "Custom Software Development",
    excerpt: "Build the systems your business runs on — engineered to your process, owned by your team, and easy to change.",
    capabilities: ["Enterprise Applications", "Web Applications", "REST APIs", "Microservices"],
    outcomes: ["Software that fits how you actually work", "Lower cost of change over time", "No lock-in to a rigid off-the-shelf tool"],
    span: "lg:col-span-5",
  },
  {
    slug: "ai-automation",
    icon: Bot,
    title: "AI & Automation",
    excerpt: "Put AI to work on the tasks that drain your teams — with the governance and oversight an enterprise requires.",
    capabilities: ["AI Integration", "Chatbots", "Generative AI", "Intelligent Automation"],
    outcomes: ["Manual, repetitive work automated", "Faster, better-informed decisions", "Round-the-clock customer response"],
    span: "lg:col-span-7",
  },
  {
    slug: "data-engineering",
    icon: Database,
    title: "Data Engineering",
    excerpt: "Give leaders numbers they can trust — governed pipelines and a warehouse that turn scattered data into decisions.",
    capabilities: ["ETL", "Data Integration", "Data Warehousing", "Analytics"],
    outcomes: ["One trusted source of truth", "Decisions based on current data", "Reporting your teams rely on"],
    span: "lg:col-span-4",
  },
  {
    slug: "devops",
    icon: GitBranch,
    title: "DevOps",
    excerpt: "Ship changes safely and often — with the automation and visibility to release without holding your breath.",
    capabilities: ["Docker", "Kubernetes", "CI/CD"],
    outcomes: ["Releases without downtime", "Shorter path from idea to production", "Clear visibility into what’s running"],
    span: "lg:col-span-4",
  },
  {
    slug: "managed-services",
    icon: LifeBuoy,
    title: "Managed Services",
    excerpt: "Keep critical platforms running with proactive, SLA-backed operation — so your team can focus on the business.",
    capabilities: ["24/7 Monitoring", "Incident Response", "Platform Support", "Continuous Optimization"],
    outcomes: ["Uptime you can plan around", "Lower operational overhead", "Issues caught before users notice"],
    span: "lg:col-span-4",
  },
  {
    slug: "technology-consulting",
    icon: Compass,
    title: "Technology Consulting",
    excerpt: "Make confident technology decisions — independent strategy and architecture advice tied to business outcomes.",
    capabilities: ["Digital Strategy", "Architecture Review", "Modernization Roadmaps", "Executive Advisory"],
    outcomes: ["A roadmap the board can back", "Delivery risk understood upfront", "Technology aligned to business goals"],
    span: "lg:col-span-12",
  },
];

export const INDUSTRIES = [
  { icon: Landmark, name: "Financial Services", desc: "Modernize banking, insurance and fintech platforms without compromising on security, resilience or regulatory obligations." },
  { icon: HeartPulse, name: "Healthcare & Life Sciences", desc: "Connect fragmented systems and unlock data responsibly to improve outcomes, while respecting patient privacy at every step." },
  { icon: ShoppingCart, name: "Retail & E-commerce", desc: "Trade confidently through peak demand with commerce platforms built for scale, personalization and margin." },
  { icon: Factory, name: "Manufacturing", desc: "Bring plant-floor and enterprise data together to reduce downtime, improve quality and run leaner operations." },
  { icon: Truck, name: "Logistics & Supply Chain", desc: "Gain real-time visibility across the chain and automate the handoffs that slow fulfilment and inflate cost." },
  { icon: Building2, name: "Public Sector", desc: "Deliver dependable, citizen-first digital services on modern foundations, built for transparency and accountability." },
];

export const TECHNOLOGIES = [
  { group: "Cloud", items: ["AWS", "Azure", "Google Cloud"] },
  { group: "Integration", items: ["MuleSoft", "Apache Kafka", "REST", "GraphQL"] },
  { group: "Languages", items: ["Java", "Python", "TypeScript", "Go"] },
  { group: "Data", items: ["Snowflake", "PostgreSQL", "MongoDB", "Databricks"] },
  { group: "DevOps", items: ["Docker", "Kubernetes", "Terraform", "GitHub Actions"] },
  { group: "AI", items: ["OpenAI", "LangChain", "TensorFlow", "Hugging Face"] },
];

export const TECH_MARQUEE = [
  "AWS", "Azure", "Google Cloud", "MuleSoft", "Kubernetes", "Docker", "Kafka",
  "Snowflake", "Python", "TypeScript", "Terraform", "GraphQL", "MongoDB", "OpenAI",
];

export const PROCESS = [
  { no: "01", title: "Understand", desc: "We start with your business, not your tech stack — agreeing the outcomes that matter and the constraints we must respect before proposing anything." },
  { no: "02", title: "Shape", desc: "We design the architecture and a sequenced plan that delivers value early and keeps risk visible, so stakeholders can commit with confidence." },
  { no: "03", title: "Deliver", desc: "Senior, embedded teams build in short increments with quality, security and testing in place from the first commit — never bolted on later." },
  { no: "04", title: "Sustain", desc: "We operate, measure and refine what we’ve built, and leave your teams equipped to own it — value that compounds long after we’re gone." },
];

export const WHY_US = [
  { icon: Users, title: "Senior people, on your problem", desc: "The practitioners who scope your work are the ones who deliver it. No hand-offs to a junior bench." },
  { icon: Gauge, title: "Measured on outcomes", desc: "We define success in business terms at the outset, and we hold ourselves to it — not to activity or story points." },
  { icon: ShieldCheck, title: "Built to pass audit", desc: "Security, compliance and resilience are designed in from day one, aligned to your industry’s obligations." },
  { icon: Layers, title: "Reuse over rebuild", desc: "We favour reusable, composable assets so today’s investment keeps paying back across the organization." },
  { icon: Rocket, title: "Modern by default", desc: "Cloud-native, automated and observable — the standard we’d want if the platform were our own." },
  { icon: LineChart, title: "Designed to scale with you", desc: "Architectures that grow with the business and avoid the costly rewrites that stall so many programs." },
];

export const SOLUTIONS = [
  { title: "Integration Accelerator", desc: "A reusable API framework that connects your core systems in weeks — turning a stalled integration backlog into shared assets.", tag: "Integration" },
  { title: "Cloud Modernization", desc: "A pragmatic path off legacy platforms that cuts run cost and removes the fragility holding back your roadmap.", tag: "Cloud" },
  { title: "Enterprise AI Assistants", desc: "Governed, domain-aware AI embedded into real workflows — automating routine work while keeping humans in control.", tag: "AI" },
];

// PLACEHOLDER case studies — replace with real client work when available.
// NOTE: All metrics below are ILLUSTRATIVE placeholders for layout only. Replace with verified results.
export const CASE_STUDIES = [
  {
    slug: "global-bank-integration",
    title: "Turning an integration backlog into reusable assets",
    industry: "Financial Services",
    summary: "How a banking group replaced brittle, one-off integrations with a governed API layer — and shortened partner onboarding from weeks to days.",
    image: "https://images.pexels.com/photos/7869103/pexels-photo-7869103.jpeg?auto=compress&cs=tinysrgb&w=1200",
    tags: ["MuleSoft", "API Management"],
    client: "Global banking group (placeholder)",
    timeline: "Illustrative · multi-quarter program",
    services: ["Enterprise Integration", "API Management"],
    overview: "Every new product and partner required bespoke integration work against core banking and dozens of downstream systems. Delivery was slow, costly and fragile — and the backlog kept growing.",
    challenge: [
      "Years of point-to-point connections meant a single change could ripple unpredictably across the estate, making every release a risk.",
      "Nothing was reused. Each integration was rebuilt from scratch, so cost and lead time grew with every new product and partner.",
    ],
    approach: [
      { title: "Agree the outcome", desc: "Aligned leadership on a measurable goal — faster, cheaper onboarding — and mapped the estate before proposing an architecture." },
      { title: "Design for reuse", desc: "Introduced an API-led model with governed, reusable APIs so each connection built value the whole organization could draw on." },
      { title: "Enable the teams", desc: "Stood up an operating model and standards so internal teams could discover and consume APIs safely, without us in the loop." },
    ],
    results: [
      { value: "40+", label: "systems unified behind governed APIs" },
      { value: "Weeks → days", label: "to onboard a new partner" },
      { value: "Reuse", label: "became the default, not the exception" },
    ],
    techStack: ["MuleSoft", "API Management", "OAuth 2.0", "Kafka"],
    quote: { text: "The backlog we’d carried for years became a set of assets the whole organization now builds on.", author: "Programme sponsor (placeholder)" },
  },
  {
    slug: "retail-cloud-migration",
    title: "Trading through peak without holding your breath",
    industry: "Retail & E-commerce",
    summary: "A retailer moved off an ageing monolith to a cloud-native platform — gaining elastic scale for peak season and a faster path to new features.",
    image: "https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg?auto=compress&cs=tinysrgb&w=1200",
    tags: ["AWS", "Microservices"],
    client: "Omnichannel retailer (placeholder)",
    timeline: "Illustrative · phased migration",
    services: ["Cloud Consulting", "Custom Software Development"],
    overview: "A monolithic commerce platform buckled under seasonal spikes and slowed every release, putting peak-season revenue at risk and frustrating customers.",
    challenge: [
      "The platform couldn’t scale on demand, forcing expensive over-provisioning that still left outages on the table during peak events.",
      "Tightly coupled code made releases slow and risky, capping the pace of the customer-facing improvements the business needed.",
    ],
    approach: [
      { title: "Establish firm ground", desc: "Built a secure, well-architected cloud foundation with guardrails, monitoring and cost controls from day one." },
      { title: "Migrate without drama", desc: "Extracted capabilities into services incrementally — no risky big-bang cutover, value delivered along the way." },
      { title: "Make releasing routine", desc: "Introduced autoscaling and automated pipelines so the team could release often, and confidently, through peak." },
    ],
    results: [
      { value: "Elastic", label: "scale that absorbs peak demand" },
      { value: "Lower", label: "run cost than the over-provisioned estate" },
      { value: "Frequent", label: "confident releases, not quarterly events" },
    ],
    techStack: ["AWS", "Kubernetes", "Microservices", "Terraform"],
    quote: { text: "For the first time, peak season is something we plan for — not something we survive.", author: "Head of Digital (placeholder)" },
  },
  {
    slug: "ai-service-automation",
    title: "Freeing skilled teams from routine service work",
    industry: "Healthcare",
    summary: "A provider deployed a governed AI assistant that handled routine enquiries — cutting wait times while keeping sensitive data compliant and auditable.",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=70",
    tags: ["Generative AI", "Automation"],
    client: "Healthcare provider (placeholder)",
    timeline: "Illustrative · pilot to production",
    services: ["AI & Automation", "Enterprise Integration"],
    overview: "High volumes of repetitive enquiries consumed skilled staff time and lengthened wait times — while strict privacy rules ruled out any solution without full oversight.",
    challenge: [
      "Routine questions absorbed the time of people whose expertise was needed elsewhere, and response times suffered as a result.",
      "Any AI had to meet strict privacy and compliance requirements, with every answer traceable and reviewable.",
    ],
    approach: [
      { title: "Ground it in the truth", desc: "Built an assistant grounded in approved knowledge, with guardrails and human review for anything sensitive." },
      { title: "Keep data in bounds", desc: "Connected to core systems through governed APIs so information never left compliant boundaries." },
      { title: "Prove it, then scale", desc: "Instrumented quality and safety, piloted in a contained setting, and expanded only once the numbers held up." },
    ],
    results: [
      { value: "Routine work", label: "handled automatically, end to end" },
      { value: "Shorter", label: "wait times on common enquiries" },
      { value: "Auditable", label: "and compliant by design" },
    ],
    techStack: ["OpenAI", "LangChain", "Vector DB", "REST APIs"],
    quote: { text: "Our specialists spend their time on the cases that genuinely need a human — which is the point.", author: "Director of Operations (placeholder)" },
  },
  {
    slug: "data-platform-modernization",
    title: "Giving every plant a single set of numbers",
    industry: "Manufacturing",
    summary: "A manufacturer replaced scattered, batch-only reporting with governed pipelines and a warehouse — delivering trusted, near-real-time operational insight.",
    image: "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&w=1200",
    tags: ["Data Engineering", "Snowflake"],
    client: "Manufacturer (placeholder)",
    timeline: "Illustrative · foundational build",
    services: ["Data Engineering", "Technology Consulting"],
    overview: "Data lived in silos across plants and systems. Leaders couldn’t trust the reports in front of them, and by the time numbers arrived, they were already out of date.",
    challenge: [
      "Fragmented, inconsistent data eroded trust in reporting and slowed every operational decision.",
      "Batch-only pipelines meant leaders were always acting on yesterday’s picture, never today’s.",
    ],
    approach: [
      { title: "Set the foundation", desc: "Designed a governed warehouse with clear ownership and quality checks, so trust was built into the architecture." },
      { title: "Make it reliable", desc: "Built automated, observable pipelines integrating plant and enterprise sources without manual intervention." },
      { title: "Put it in reach", desc: "Delivered curated, trusted data products powering dashboards teams could actually rely on." },
    ],
    results: [
      { value: "One", label: "source of truth across every plant" },
      { value: "Near real-time", label: "operational insight, not next-day" },
      { value: "Trusted", label: "reporting leaders act on with confidence" },
    ],
    techStack: ["Snowflake", "Databricks", "Airflow", "dbt"],
    quote: { text: "Every site now reads from the same numbers — the arguments about whose data is right simply stopped.", author: "Operations lead (placeholder)" },
  },
];

// PLACEHOLDER testimonials — replace with real, attributable client quotes.
// Attribution intentionally generic until permission to name clients is secured.
export const TESTIMONIALS = [
  { quote: "They brought senior people who understood our business, not just our technology — and a program that had stalled for years finally moved.", name: "Client reference", role: "Group CTO · Financial Services (placeholder)" },
  { quote: "The work was pragmatic and transparent throughout. We always knew where we stood, and the outcomes were exactly what we agreed at the start.", name: "Client reference", role: "VP of Engineering · Enterprise (placeholder)" },
  { quote: "What stood out was the handover. They left our teams genuinely able to own and extend what had been built.", name: "Client reference", role: "Director of Digital · Retail (placeholder)" },
];

export const FAQS = [
  { q: "How does a typical engagement begin?", a: "With a short, focused consultation. We listen to the outcome you’re chasing, assess the landscape, and come back with a pragmatic first step — before any commitment to a larger program." },
  { q: "You’re a new firm — why should we trust you with critical work?", a: "Because the people you meet are the people who deliver. Our consultants have led mission-critical programs at scale, and we’re deliberately structured to keep senior practitioners close to your problem." },
  { q: "Do you replace our teams or work alongside them?", a: "We work alongside them. We embed with your people, transfer knowledge as we go, and aim to leave you more capable — not more dependent." },
  { q: "How do you keep programs from over-running?", a: "We sequence work to deliver value early and keep risk visible. Short increments, agreed outcomes and honest status reporting mean surprises surface early, when they’re cheap to fix." },
  { q: "Which platforms and clouds do you work across?", a: "We’re independent and pragmatic — working across AWS, Azure and Google Cloud, and recommending the right fit for your workloads rather than a preferred vendor." },
  { q: "How do you handle security and compliance?", a: "They’re designed in from the first day, aligned to your industry’s obligations, and built to stand up to audit — never treated as a step at the end." },
];

// PLACEHOLDER blog posts — replace with real articles.
export const BLOG_POSTS = [
  {
    slug: "api-led-connectivity-explained",
    title: "API-led connectivity, explained for the boardroom",
    category: "Integration",
    date: "2025-01-15",
    read: "6 min",
    author: "FusionTech Consulting",
    excerpt: "Why reusable APIs change the economics of change — and how to start without a multi-year program.",
    image: "https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: [
      { type: "p", text: "For most large organizations, the biggest barrier to moving quickly isn’t ambition — it’s integration. Years of point-to-point connections leave a fragile web that’s expensive to change and risky to touch. API-led connectivity offers a way out." },
      { type: "h2", text: "What it actually means" },
      { type: "p", text: "Instead of wiring systems directly together, API-led connectivity organizes integration into reusable layers: system APIs that safely expose core data, process APIs that orchestrate business logic, and experience APIs that shape data for each channel." },
      { type: "list", items: ["System APIs expose underlying systems consistently", "Process APIs compose and orchestrate business capabilities", "Experience APIs tailor data for web, mobile and partners"] },
      { type: "h2", text: "Why it matters to the business" },
      { type: "p", text: "The shift from bespoke integration to reusable assets changes the economics of change. Once an API exists, the next team consumes it in days rather than rebuilding it — and that saving compounds across the organization." },
      { type: "quote", text: "The value of an API isn’t the first time you use it. It’s every time after." },
      { type: "h2", text: "Where to start" },
      { type: "p", text: "Begin with one high-value domain — the systems everyone wants to connect to. Wrap them in well-governed APIs and set a light operating model so teams reuse what already exists. You don’t need a multi-year program to see the first returns." },
    ],
  },
  {
    slug: "generative-ai-in-the-enterprise",
    title: "Adopting generative AI without losing control",
    category: "AI",
    date: "2025-01-08",
    read: "8 min",
    author: "FusionTech Consulting",
    excerpt: "A pragmatic framework for putting AI to work in the enterprise — without compromising governance or trust.",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=70",
    content: [
      { type: "p", text: "Generative AI has moved from novelty to necessity, but enterprise adoption raises real questions about accuracy, privacy and control. The organizations succeeding aren’t moving recklessly — they’re moving deliberately." },
      { type: "h2", text: "Start with the problem, not the model" },
      { type: "p", text: "The most valuable initiatives begin with a specific workflow: summarizing case notes, drafting responses, retrieving policy answers. Anchoring to a concrete outcome keeps scope honest and value measurable." },
      { type: "h2", text: "Ground it in your own knowledge" },
      { type: "p", text: "Grounding responses in your approved, current content dramatically reduces error and keeps every answer traceable to a source — the difference between a demo and something you can put in front of customers." },
      { type: "list", items: ["Curate and govern what the model can access", "Keep sensitive data within compliant boundaries", "Add human review for anything high-stakes"] },
      { type: "quote", text: "Trust is the real product. Get governance right and adoption follows." },
      { type: "h2", text: "Measure, then scale" },
      { type: "p", text: "Instrument quality and safety from day one. Pilot in a contained setting, learn from real usage, and expand only once the results hold up. Confidence, not enthusiasm, is what earns the next workflow." },
    ],
  },
  {
    slug: "cloud-cost-optimization",
    title: "Five levers for cloud cost that don’t cost performance",
    category: "Cloud",
    date: "2024-12-20",
    read: "5 min",
    author: "FusionTech Consulting",
    excerpt: "Practical ways to bring cloud spend under control while improving resilience — not trading one for the other.",
    image: "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&w=1200",
    content: [
      { type: "p", text: "Cloud bills have a way of creeping upward. The good news: most organizations can meaningfully reduce spend without sacrificing performance — often while improving it. Here are five levers worth pulling." },
      { type: "h2", text: "1. Right-size relentlessly" },
      { type: "p", text: "Most workloads are over-provisioned. Match instance types to real utilization, and let autoscaling handle variable demand instead of paying for static headroom you rarely use." },
      { type: "h2", text: "2. Commit where it’s predictable" },
      { type: "p", text: "For steady baseline workloads, commitment-based pricing can cut costs substantially against on-demand rates — with no change to how anything runs." },
      { type: "h2", text: "3. Retire what’s idle" },
      { type: "list", items: ["Shut down non-production environments out of hours", "Remove unattached storage and unused load balancers", "Set lifecycle policies on logs and backups"] },
      { type: "h2", text: "4. Architect for elasticity" },
      { type: "p", text: "Serverless and autoscaling let you pay for what you use, not what you might need at peak. Elasticity is a cost strategy and a resilience strategy at once." },
      { type: "h2", text: "5. Make cost visible" },
      { type: "quote", text: "You can’t optimize what teams can’t see. Attribute spend to the people who create it." },
      { type: "p", text: "Put cost in front of the engineers who influence it, and optimization stops being an annual project and becomes an ongoing habit." },
    ],
  },
  {
    slug: "kubernetes-for-enterprise-platforms",
    title: "When Kubernetes earns its place — and when it doesn’t",
    category: "DevOps",
    date: "2024-12-10",
    read: "7 min",
    author: "FusionTech Consulting",
    excerpt: "A clear-eyed view of where Kubernetes genuinely pays off in the enterprise, and where simpler wins.",
    image: "https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: [
      { type: "p", text: "Kubernetes has a reputation for complexity, and not without reason. For organizations running many services at scale, it remains the most capable foundation for portable, resilient platforms. The skill is knowing when the trade-off is worth it." },
      { type: "h2", text: "What it gives you" },
      { type: "list", items: ["Consistent deployment and scaling across environments", "Self-healing workloads and zero-downtime releases", "A mature ecosystem for security and observability"] },
      { type: "h2", text: "When it’s overkill" },
      { type: "p", text: "For a handful of simple services, managed platforms or serverless get you further, faster. Kubernetes pays off when scale and standardization outweigh the operational overhead — not before." },
      { type: "quote", text: "Adopt Kubernetes for the problems you have, not the ones you might have one day." },
      { type: "h2", text: "Making it manageable" },
      { type: "p", text: "The complexity is real but tameable: use a managed control plane, codify everything, and build clear paths so product teams ship without becoming cluster experts." },
    ],
  },
  {
    slug: "modern-data-stack-elt-and-trust",
    title: "The modern data stack is really about trust",
    category: "Data",
    date: "2024-11-28",
    read: "6 min",
    author: "FusionTech Consulting",
    excerpt: "The move from ETL to ELT changed the plumbing. Earning trust in the numbers is the part that actually matters.",
    image: "https://images.pexels.com/photos/7869103/pexels-photo-7869103.jpeg?auto=compress&cs=tinysrgb&w=1200",
    content: [
      { type: "p", text: "Cloud warehouses reshaped how organizations handle data. Extract-transform-load has largely given way to extract-load-transform — but the plumbing was never the hard part. Trust is." },
      { type: "h2", text: "Why ELT took over" },
      { type: "p", text: "Modern warehouses separate storage from compute and scale on demand. Loading raw data first, then transforming it in place, is faster to build, easier to iterate on, and keeps the source available for reprocessing." },
      { type: "h2", text: "Trust is the hard part" },
      { type: "list", items: ["Give every data product a clear owner", "Test data quality continuously, not just at launch", "Document lineage so consumers know where numbers come from"] },
      { type: "quote", text: "A dashboard nobody trusts is worse than no dashboard at all." },
      { type: "h2", text: "Start from a decision" },
      { type: "p", text: "The best data platforms start from a decision someone needs to make. Work back from that to the metrics and pipelines required — and resist the urge to boil the ocean." },
    ],
  },
  {
    slug: "choosing-an-integration-platform",
    title: "Buy an integration platform, or build your own?",
    category: "Integration",
    date: "2024-11-14",
    read: "5 min",
    author: "FusionTech Consulting",
    excerpt: "A practical way to decide when a dedicated platform beats hand-rolled connectors — before the maintenance bill arrives.",
    image: "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&w=1200",
    content: [
      { type: "p", text: "Every integration decision reaches the same fork: adopt a dedicated platform, or build connectors in-house. Both can be right — it depends on scale, governance and the shape of your team." },
      { type: "h2", text: "The case for a platform" },
      { type: "list", items: ["Prebuilt connectors accelerate common integrations", "Centralized governance, security and monitoring", "Reusable assets and a clear operating model"] },
      { type: "h2", text: "The case for building" },
      { type: "p", text: "For a small number of bespoke integrations, custom code stays lean and avoids licensing. The risk is that ’a few’ quietly becomes dozens, and the maintenance burden compounds out of sight." },
      { type: "quote", text: "The question isn’t ’can we build it?’ It’s ’do we want to own it for the next decade?’" },
      { type: "h2", text: "A pragmatic default" },
      { type: "p", text: "As volume and governance needs grow, a platform usually wins on total cost of ownership. Forecast your real integration roadmap honestly — not just today’s shortlist — and decide from there." },
    ],
  },
];

// PLACEHOLDER roles — replace with live openings.
export const JOBS = [
  { title: "Senior Integration Consultant (MuleSoft)", location: "Remote", type: "Full-time", team: "Integration" },
  { title: "Cloud Solutions Architect", location: "Remote", type: "Full-time", team: "Cloud" },
  { title: "AI/ML Engineer", location: "Remote", type: "Full-time", team: "AI & Automation" },
  { title: "DevOps Engineer", location: "Remote", type: "Full-time", team: "DevOps" },
  { title: "Data Engineer", location: "Remote", type: "Full-time", team: "Data" },
];

export const VALUES = [
  { title: "Craft", desc: "We hold our work to a standard we’re proud to put our name on — and we don’t cut corners you can’t see." },
  { title: "Candour", desc: "We tell clients what they need to hear, not what’s easiest to say. Trust is built on honest status, always." },
  { title: "Pragmatism", desc: "We choose the simplest solution that delivers durable value, not the most impressive-sounding one." },
  { title: "Ownership", desc: "We treat your outcomes as our own, and we’re not finished until your teams can carry the work forward." },
];
